import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChatInput, Icons } from '@usepepper/sprout-kit-ui';
import { useAgUiInterrupts } from '@assistant-ui/react-ag-ui';
import { useAui, useAuiState } from '@assistant-ui/store';
import { useOpenThread } from '@/experiences/sales-portal/domain/superintelligence/blocs/hooks/use-open-thread';
import { useSuperintelligenceContext } from '@/experiences/sales-portal/domain/superintelligence/containers/superintelligence-runtime-provider';
import { useTheme } from '@/contexts/theme-context';

const SEND_ICON_SIZE = 20;

// Same cap as the copilot composer: oversized messages stress the backend and can freeze the client.
const MAX_MESSAGE_LENGTH = 2000;

const placeholderKey = ({
  archived,
  awaitingApproval,
  runInFlight,
}: {
  archived: boolean;
  awaitingApproval: boolean;
  runInFlight: boolean;
}) => {
  if (archived) {
    return 'sales_portal.superintelligence.placeholder_archived';
  }
  if (runInFlight) {
    return 'sales_portal.superintelligence.placeholder_run_in_flight';
  }
  if (awaitingApproval) {
    return 'sales_portal.superintelligence.placeholder_awaiting_approval';
  }
  return 'sales_portal.superintelligence.placeholder';
};

export const AgentComposer: React.FC = () => {
  const theme = useTheme();
  const aui = useAui();
  const composerText = useAuiState((s) => s.composer.text);
  const [text, setText] = useState(composerText);
  const lastWrittenText = useRef(composerText);
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const awaitingApproval = useAgUiInterrupts().length > 0;
  const { isLoadingThread, runInFlight } = useSuperintelligenceContext();
  const { archived } = useOpenThread();
  const disabled =
    awaitingApproval || archived || runInFlight || isLoadingThread;
  const placeholder = i18n.t(
    placeholderKey({ archived, awaitingApproval, runInFlight }),
  );

  useEffect(() => {
    if (composerText !== lastWrittenText.current) {
      lastWrittenText.current = composerText;
      setText(composerText);
    }
  }, [composerText]);

  const handleChangeText = useCallback(
    (value: string) => {
      lastWrittenText.current = value;
      setText(value);
      aui.composer().setText(value);
    },
    [aui],
  );
  const handleSend = useCallback(() => aui.composer().send(), [aui]);

  return (
    <ChatInput
      aria-label={placeholder}
      disabled={disabled}
      maxLength={MAX_MESSAGE_LENGTH}
      onChangeText={handleChangeText}
      onSend={handleSend}
      placeholder={placeholder}
      sendIcon={
        <Icons.ArrowUp
          color={theme.sk.color.icon.ondark}
          size={SEND_ICON_SIZE}
        />
      }
      sending={isRunning}
      value={text}
    />
  );
};
